import React, { useState, useRef, useEffect, forwardRef,useImperativeHandle, } from "react";
import { Box, IconButton, Typography, TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ButtonReusable from "./ButtonReusable";
import { useQuery } from "@apollo/client"; 
import { GET_CONTACT_NOTES_BY_ID } from "./Graphql/ContactsQueries/contactsQuery";
import ReusableSpinner from "../components/ReactTable/ReusableSpinner";
import { formatDateTimeWithHours, formatUserName, getNowDate } from "../utils";

const NotesReusable = forwardRef(({ data, setState, notes: initialNotes = [],onNotesChange, contact_id, state }, ref,deleted) => {

  const [notes, setNotes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const editRef = useRef(null);
  const seededFromProp = useRef(false);
 const [validationError, setValidationError] = useState("");
  const { loading: loadingContactNotes, data: ContactNotesData ,refetch} = useQuery(
    GET_CONTACT_NOTES_BY_ID,
    {
      variables: { contactId: contact_id },
        skip : contact_id == null,
      context: {
        headers: {
          authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      },
    }
  );

//  Expose validate function to parent
  useImperativeHandle(ref, () => ({
    validateNotes: () => {
      // if no note is being edited -> consider valid
      if (!editingId) {
        setValidationError("");
        return true;
      }

      const currentNote = notes.find(
        (n) => (n.arcimedes_row_id || n.temp_id) === editingId
      );

      if (currentNote && currentNote.text.trim() === "") {
        setValidationError("Note cannot be empty");
        return false;
      }

      setValidationError("");
      return true;
    },
  }));

  useEffect(()=>{
if(state.success === "Saved"){
   setEditingId(null);
   refetch()
}
  },[state])

  useEffect(() => {
  if (contact_id) {
    refetch();
  }
}, [contact_id, refetch]);
  
   useEffect(() => {
    if (onNotesChange) {
      onNotesChange(notes,editingId);
    }
  }, [notes, onNotesChange]);

  // sync server data
  useEffect(() => {
    const serverNotes = ContactNotesData && ContactNotesData.getContactNotesByContactId;
    if (Array.isArray(serverNotes) && serverNotes.length > 0) {
      // Structured notes from the contact_notes table are the source of truth.
      setNotes(serverNotes);
      seededFromProp.current = false;
    } else if (
      !seededFromProp.current &&
      Array.isArray(initialNotes) &&
      initialNotes.length > 0
    ) {
      // Fall back to notes provided by the parent (from getContactByContactid) when the
      // contact has no contact_notes rows yet — e.g. legacy notes stored on the
      // CONTACTS.notes column, which are surfaced with a null arcimedes_row_id. Guarded so
      // the parent's onNotesChange round-trip can't re-seed and loop.
      setNotes(initialNotes);
      seededFromProp.current = true;
    }
  }, [ContactNotesData, initialNotes]);

  const storedUserData = JSON.parse(sessionStorage.getItem("userData") || "{}");
  const user_id = String(storedUserData.id) || "";

const fullName = formatUserName({
  firstName: storedUserData.firstName,
  lastName: storedUserData.lastName,
  username: storedUserData.username,
});

  // Add new note
const handleAddNote = () => {
  if (editingId) return;
  const newNote = {
    temp_id: Date.now(),         // temporary unique key for UI
    user_id,
    user_name: fullName,
    text: "",
    date: getNowDate(),
    deleted: null,
  };
  setNotes((prev) => [newNote, ...prev]);
  setEditingId(newNote.temp_id);
  setState((prev) => ({
    ...prev,
    viewMode: false,
    success:null
  }));
};

// Edit note text and update date
const handleChange = (id, value) => {
  setNotes(prevNotes =>
    prevNotes.map(note => {
      const currentId = note.arcimedes_row_id || note.temp_id;
      if (currentId === id) {
        return { 
          ...note, 
          text: value, 
          date: getNowDate(), 
          user_name: fullName 
        };
      }
      return note;
    })
  );
   if (value.trim() === "") {
      setValidationError("Note cannot be empty");

    } else {
      setValidationError("");
    }
};



// Delete note (soft delete)
const handleDeleteMessage = (note) => {
  if (note.user_id !== user_id) return; // prevent action
    setState((prev) => ({
    ...prev,
    viewMode: false,
  }));
  const id = note.arcimedes_row_id !== null && note.arcimedes_row_id !== undefined
    ? note.arcimedes_row_id
    : note.temp_id;

  setNotes(prevNotes => prevNotes.map(n => {
    const currentId = n.arcimedes_row_id !== null && n.arcimedes_row_id !== undefined
      ? n.arcimedes_row_id
      : n.temp_id;

    if (currentId === id) {
      return { ...n, deleted: 1 };
    }
    return n;
  }));
  setEditingId(null)
};
;



 useEffect(() => {
    const handleClickOutside = (event) => {
      if (editRef.current && !editRef.current.contains(event.target)) {
        const currentNote = notes.find(
          (n) => (n.arcimedes_row_id || n.temp_id) === editingId
        );
        if (currentNote && currentNote.text.trim() === "") {
          setValidationError("Note cannot be empty");
          return; // don't close edit mode if empty
        }
        setEditingId(null);
        setValidationError("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editingId, notes]);

useEffect(() => {
  setState((prev) => ({
    ...prev,
    notes: notes,  //  append latest notes into parent state
  }));
}, [notes, data]);
  return (
    <React.Fragment>
      {loadingContactNotes ? (
        <ReusableSpinner />
      ) : (
        <div>
          <ButtonReusable onClick={handleAddNote} disabled={!!editingId || data.deleted == 1}>
            Add Notes
          </ButtonReusable>
          {notes
            .filter((note) => note.deleted !== 1)
            .map((note) => (
              <Box
                key={note.arcimedes_row_id || note.temp_id}
                sx={{
                  backgroundColor: "white",
                  border: "1px solid green",
                  color: "black",
                  width: "800px",
                  maxWidth: "100%",
                  mb: 2,
                  borderRadius: "6px",
                  p: 1.5,
                  boxShadow: 1,
                }}
              >
                {/* Header: date + username + actions */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", fontSize: "13px" }}
                  >
                   {formatDateTimeWithHours(note.arcimedes_modified ? note.arcimedes_modified : note.date)} {note.user_name}:
                  </Typography>

                  {/* {note.user_id === user_id && ( */}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      size="small"
                      disabled={note.user_id !== user_id || data.deleted == 1}
                      onClick={() => {
                        if (note.user_id !== user_id) return; // prevent action
                        const id = note.arcimedes_row_id || note.temp_id; // support both
                        setEditingId(id);
                        setState((prev) => ({
                          ...prev,
                          viewMode: false,
                          success: null,
                        }));
                      }}
                      sx={{
                        color:
                          note.user_id !== user_id || data.deleted == 1 ? "grey.400" : "#90C54B",
                      }}
                    >
                      <EditIcon
                        disabled={note.user_id !== user_id || data.deleted == 1}
                       sx={{
                        color:
                          note.user_id !== user_id|| data.deleted == 1 ? "grey.400" : "#90C54B",
                      }}
                        fontSize="small"
                      />
                    </IconButton>

                    <IconButton
                      size="small"
                      disabled={note.user_id !== user_id || data.deleted == 1}
                      onClick={() => handleDeleteMessage(note)}
                      sx={{
                        color: note.user_id !== user_id || data.deleted == 1? "grey.400" : "red",
                      }}
                    >
                      <DeleteIcon
                        disabled={note.user_id !== user_id || data.deleted == 1}
                        sx={{
                          color: note.user_id !== user_id || data.deleted == 1 ? "grey.400" : "red",
                        }}
                        fontSize="small"
                      />
                    </IconButton>
                  </Box>
                  {/* )} */}
                </Box>

                {/* Body: editable text or static */}
                <Box
                ref={editingId === (note.arcimedes_row_id || note.temp_id) ? editRef : null}
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: "14px",
                  }}
                >
                  {editingId === (note.arcimedes_row_id || note.temp_id) ? (
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      type="text"
                      value={note.text}
                      onChange={(e) =>
                       handleChange(note.arcimedes_row_id || note.temp_id, e.target.value)
                      }
                      placeholder="Type your notes here..."
                      error={!!validationError}
                      helperText={validationError}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          borderRadius: "6px",
                          "& fieldset": { borderColor: "green" },
                          "&:hover fieldset": { borderColor: "darkgreen" },
                          "&.Mui-focused fieldset": {
                            borderColor: "green",
                            borderWidth: 2,
                          },
                        },
                        "& textarea": { resize: "none" },
                      }}
                    />
                  ) : (
                    <Typography variant="body2">{note.text}</Typography>
                  )}
                </Box>
              </Box>
            ))}
        </div>
      )}
    </React.Fragment>
  );
});

export default NotesReusable;
